import { strideAccountParser } from 'stridejs'
import { Any } from 'cosmjs-types/google/protobuf/any'
import { BaseAccount } from 'cosmjs-types/cosmos/auth/v1beta1/auth'
import { Account } from '@cosmjs/stargate'
import { Uint64 } from '@cosmjs/math'
import { injectiveAccountParser } from '@injectivelabs/sdk-ts'
import { assert } from '@/utils'
import { decodePubkey } from '../haqq/pubkey'
import { EthAccount } from './ethermint'

// Cosmos SDK 0.54 (Stride v33+, 2026-08-07) assigns account numbers via `GenerateID`,
// which forces the top bit on. Every account created since then has a number >= 2^63,
// well above `Number.MAX_SAFE_INTEGER`. `Uint64.toNumber()` throws on those and
// `Number()` silently rounds, which would produce a signature the chain rejects.
//
// cosmjs 0.32 types `Account.accountNumber` as `number`, but at runtime its direct
// sign path wraps the value in `BigInt()` and its amino sign path (patched, see
// patches/@cosmjs__amino@0.32.4.patch) stringifies it, so a `bigint` flows through
// intact. The cast goes away once we move to cosmjs 0.39, where this is a `bigint`
// for real. See FIX.md.
function toAccountNumber(value: bigint | string | number): number {
  return BigInt(value) as unknown as number
}

function accountFromBaseAccount(input: BaseAccount): Account {
  const { address, pubKey, accountNumber, sequence } = input

  const pubkey = pubKey ? decodePubkey(pubKey) : null

  return {
    address,
    pubkey,
    accountNumber: toAccountNumber(accountNumber),
    sequence: Uint64.fromString(sequence.toString()).toNumber()
  }
}

export function accountParser(input: Any): Account {
  switch (input.typeUrl) {
    case '/cosmos.auth.v1beta1.BaseAccount':
      return accountFromBaseAccount(BaseAccount.decode(input.value))

    case '/injective.types.v1beta1.EthAccount': {
      return injectiveAccountParser(input)
    }

    // https://github.com/Team-Kujira/kujira.js/blob/44085a0d1c8ca3e2d4efca4daf3c2eb70fde9af4/src/registry.ts#L127
    case '/ethermint.types.v1.EthAccount': {
      const account = EthAccount.decode(input.value)
      const baseEthAccount = account.baseAccount
      assert(baseEthAccount, 'EthAccount baseAccount is undefined.')
      const pubKeyEth = baseEthAccount.pubKey

      return {
        address: baseEthAccount.address,
        pubkey: pubKeyEth
          ? {
              type: '/ethermint.crypto.v1.ethsecp256k1.PubKey',
              value: Buffer.from(pubKeyEth.value).toString('base64')
            }
          : null,
        accountNumber: toAccountNumber(baseEthAccount.accountNumber),
        sequence: Number(baseEthAccount.sequence.toString())
      }
    }

    default: {
      return strideAccountParser(input)
    }
  }
}
